import React from 'react';
import { createSelector } from 'reselect';
import { RootReducer } from './reducers';
import { Stage } from './components/aftersale-card';
import {
    AFTERSALE_OP_TYPE,
    AFTERSALE_STAGES,
    AFTERSALE_STAGE_ENTITIES
} from 'pages/newaftersale/detail/constants';
import { AftersaleStageInfo, Detail } from './types';
import { counter } from 'utils/slardar';
import { getRetInsInfo } from 'utils/tool';
export const selectIsPageLoading = (state: RootReducer) => state.detail.isPageLoading;

export const selectPageError = (state: RootReducer) => state.detail.error;

export const selectDetailInfo = (state: RootReducer) => state.detail.detailInfo;

export const selectIsSubmitLoading = (state: RootReducer) => state.detail.isSubmitLoading;

export const selectOrderInfo = createSelector([selectDetailInfo], (detailInfo) => {
    return {
        orderGroupId: detailInfo && detailInfo.order_group_id,
        orderId: detailInfo && detailInfo.order_id,
        orders: detailInfo ? detailInfo.orders || [] : []
    };
});
export const selectRetInsInfo = createSelector([selectDetailInfo], (detailInfo) => {
    return getRetInsInfo(detailInfo);
});

export const selectEvidenceImages = createSelector([selectDetailInfo], (detailInfo: Detail) => {
    if (!detailInfo) return [];

    const op = detailInfo.after_sale_record.find(
        (record) => record.op_type === AFTERSALE_OP_TYPE.apply
    );
    if (!op) {
        return [];
    }

    return op.evidence;
});

export const selectAllActions = createSelector([selectDetailInfo], (detailInfo) => {
    if (!detailInfo) return [];

    return detailInfo.operation_list;
});

export const selectReturnInfo = createSelector([selectDetailInfo], (detailInfo: Detail): {
    username: string;
    phone: string;
    address: string;
} => {
    return {
        username: (detailInfo && detailInfo.return_user_name) || '',
        phone: (detailInfo && detailInfo.return_mobile) || '',
        address: (detailInfo && detailInfo.return_address_desc) || ''
    };
});

// 获取售后阶段进度
export const getAftersaleStageInfo = createSelector(
    [selectDetailInfo],
    (detailInfo: Detail): AftersaleStageInfo | void => {
        if (!detailInfo) return;

        try {
            // 最后一次操作记录
            const lastOpRecord =
                detailInfo.after_sale_record[detailInfo.after_sale_record.length - 1];
            const hitKey: any = Object.keys(
                AFTERSALE_STAGE_ENTITIES[detailInfo.after_sale_type]
            ).find((key) => {
                const hit: any = AFTERSALE_STAGE_ENTITIES[detailInfo.after_sale_type][key].hit;

                // 逐个对 after_sale_status、refund_status、op_type 进行对比
                if (
                    hit[0] === detailInfo.after_sale_status &&
                    hit[2] === (lastOpRecord && lastOpRecord.op_type)
                ) {
                    if (
                        // 换货
                        detailInfo.after_sale_type === 3 ||
                        // hit refund_status 可能是个数组，如果是数组，则判断当前 refund_status 是否存在其中
                        (Array.isArray(hit[1]) && hit[1].includes(detailInfo.refund_status)) ||
                        hit[1] === detailInfo.refund_status
                    ) {
                        return true;
                    }
                }
                return false;
            });
            const hitStage = hitKey && AFTERSALE_STAGE_ENTITIES[detailInfo.after_sale_type][hitKey];

            // 没有命中已有的阶段，进行事件上报收集错误的售后状态
            // 出现没有命中阶段，说明后端的状态在 AFTERSALE_STAGE_ENTITIES 中没有定义!
            // 首先需要确定，产品逻辑中是否有该状态；
            if (!hitStage) {
                counter('error_aftersale_detail_stage', {
                    tags: {
                        err: '没有对应的售后状态',
                        orderId: detailInfo.order_id,
                        aftersaleType: detailInfo.after_sale_type as any,
                        aftersaleStatus: detailInfo.after_sale_status as any,
                        refund_status: detailInfo.refund_status as any,
                        lastOpRecordType: lastOpRecord ? lastOpRecord.op_type : 'null'
                    }
                });

                return;
            }

            // 命中已有阶段，但是不需要显示进度条
            if (hitStage.stageStyle.hiddenStages) {
                return {
                    status: hitKey,
                    stages: [],
                    cardInfo: hitStage.cardInfo(detailInfo, lastOpRecord),
                    stageStyle: hitStage.stageStyle
                };
            }

            const stages: Stage[] = AFTERSALE_STAGES[detailInfo.after_sale_type].map((name) => ({
                status: '',
                name
            }));

            // 判断当前阶段是通过，售后状态（detailInfo.after_sale_status），倒叙迭代 stages
            // 如果发现当前售后状态有对应的阶段信息，则设置；并前序的一些阶段默认为 finish
            let pass = null;
            for (let i = stages.length - 1; i >= 0; i--) {
                const curStage = stages[i];

                if (pass) {
                    curStage.status = 'finish';
                    continue;
                }

                if (hitStage.stageName === curStage.name && hitStage.stageStyle.success !== null) {
                    curStage.status = hitStage.stageStyle.success ? 'success' : 'reject';
                    pass = true;
                }
            }

            return {
                status: hitKey,
                stages,
                cardInfo: hitStage.cardInfo(detailInfo, lastOpRecord),
                stageStyle: hitStage.stageStyle
            };
        } catch (err) {
            counter('error_aftersale_detail_stage', {
                tags: {
                    err: err.message,
                    orderId: detailInfo.order_id,
                    aftersaleType: detailInfo.after_sale_type as any,
                    aftersaleStatus: detailInfo.after_sale_status as any,
                    refund_status: detailInfo.refund_status as any
                }
            });

            return;
        }
    }
);
